import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Search, Loader2, ShieldCheck, ScrollText, KeyRound, LogIn } from "lucide-react";

const ROLE_LABELS = {
  admin: "Administrator", sales_executive: "Sales Executive", sales_manager: "Sales Manager",
  design: "Design / Prepress", production_planner: "Production Planner", production_operator: "Production Operator",
  qc_inspector: "QC Inspector", store: "Store / Inventory", purchase: "Purchase Officer",
  accounts: "Accounts", hr: "HR",
};

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "", password: "" });
  const [saving, setSaving] = useState(false);

  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [u, meta] = await Promise.all([api.get("/users"), api.get("/meta/rbac")]);
      setUsers(u.data);
      setRoles(meta.data.roles);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => users.filter((u) => {
    const matchSearch = `${u.name} ${u.email}`.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === "all" || u.role === roleFilter;
    return matchSearch && matchRole;
  }), [users, search, roleFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", email: "", role: "", password: "", mobile: "", department: "" });
    setFormOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, role: u.role, password: "", mobile: u.mobile || "", department: u.department || "" });
    setFormOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.role || (!editing && (!form.email || !form.password))) {
      toast.error("Please fill all required fields");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, { name: form.name, role: form.role, mobile: form.mobile, department: form.department });
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (u) => {
    try {
      await api.patch(`/users/${u.id}/status`, { is_active: !u.is_active });
      toast.success(`User ${u.is_active ? "deactivated" : "activated"}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const doReset = async () => {
    if (resetPwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: resetPwd });
      toast.success("Password reset");
      setResetTarget(null);
      setResetPwd("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">User Management</h2>
          <p className="mt-1 text-sm text-slate-500">Manage staff accounts, roles and access.</p>
        </div>
        <div className="flex gap-2">
          <NavLink to="/roles"><Button variant="outline" data-testid="goto-roles-btn"><ShieldCheck className="mr-2 h-4 w-4" />Roles &amp; Permissions</Button></NavLink>
          <NavLink to="/login-history"><Button variant="outline" data-testid="goto-login-history-btn"><LogIn className="mr-2 h-4 w-4" />Login History</Button></NavLink>
          <NavLink to="/audit-logs"><Button variant="outline" data-testid="goto-audit-btn"><ScrollText className="mr-2 h-4 w-4" />Audit Log</Button></NavLink>
          <Button onClick={openCreate} data-testid="add-user-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Plus className="mr-2 h-4 w-4" />Add User</Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="user-search-input" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-56" data-testid="role-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Name</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Email</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Role</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Last Login</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                    <TableCell className="font-medium text-slate-800">{u.name}</TableCell>
                    <TableCell className="text-slate-600">{u.email}</TableCell>
                    <TableCell><Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700">{ROLE_LABELS[u.role] || u.role}</Badge></TableCell>
                    <TableCell>
                      {u.is_active
                        ? <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Active</Badge>
                        : <Badge className="border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-50">Inactive</Badge>}
                    </TableCell>
                    <TableCell className="text-slate-500">{u.last_login ? new Date(u.last_login).toLocaleString() : "—"}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`user-actions-${u.email}`}><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setResetTarget(u)} data-testid={`reset-user-${u.email}`}><KeyRound className="mr-2 h-4 w-4" />Reset password</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleStatus(u)} disabled={u.id === me.id}
                            className={u.is_active ? "text-rose-600 focus:text-rose-600" : "text-emerald-600 focus:text-emerald-600"}
                            data-testid={`toggle-user-${u.email}`}>
                            {u.is_active ? "Deactivate" : "Activate"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="py-12 text-center text-slate-400">No users found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Create / Edit sheet */}
      <Sheet open={formOpen} onOpenChange={setFormOpen}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{editing ? "Edit user" : "Add user"}</SheetTitle>
            <SheetDescription>{editing ? "Update the user's name and role." : "Create a new staff account with a role and temporary password."}</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="form-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!editing && (
              <div className="space-y-1.5">
                <Label>Temporary password</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Min 6 characters" data-testid="form-password-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Mobile</Label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91…" data-testid="form-mobile-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Department</Label>
              <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="e.g. Printing" data-testid="form-department-input" />
            </div>
          </div>
          <SheetFooter className="mt-8">
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-user-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editing ? "Save changes" : "Create user"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password — {resetTarget?.name}</DialogTitle>
            <DialogDescription>Set a new password for this user. They can change it later from their profile.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <Input type="text" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} placeholder="Min 6 characters" data-testid="reset-password-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button>
            <Button onClick={doReset} data-testid="confirm-reset-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">Reset password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
